import React, { FC, ReactElement, useState, useCallback, useEffect, useMemo } from 'react'
import { Box, Button, CircularProgress, Grid, Paper, Theme, useMediaQuery } from '@mui/material'
import {
  DataGridPremium,
  useGridApiRef,
  GridPaginationModel,
  gridFilteredTopLevelRowCountSelector,
  gridFilterModelSelector,
  GridPinnedColumns,
  GridRowId,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid-premium'
import { Sensor, ThingspaceSensor } from 'src/api'
import { useAddPopupMessage, useAuthorizedState, useDialog, useFillMissedUplinks, useQueryParams } from 'src/hooks/ui'
import { DialogNames, SearchParamsKeys, SensorButtons } from 'src/enums'
import { config, defaultRowsValuePerPage } from 'src/config'
import { MakeSensorsReadyToUseModal, SensorHistoricalDataModal } from 'src/components/forms'
import { TableToolbar } from 'src/components/ui'
import { useEditSensorsBleStateRequest } from 'src/hooks/api'
import { BleManageState, PopupMessage } from 'src/interfaces'
import { BulkAssignSensorsButton } from './bulkAssignSensorsButton'
import { ManageSensorBleModal } from '../manageSensorBleModal'
import { SensorQualityControlButton } from '../sensorQualityControl'

const defaultRowsPerPageOptions = defaultRowsValuePerPage
const defaultPerPage = 25
const defaultPage = 1
const initialQueryParams = {
  page: `${defaultPage}`,
  rowsPerPage: `${defaultPerPage}`,
}

interface CommonSensorListProps<Entity extends GridValidRowModel = any> {
  columns: GridColDef<Entity>[]
  data: Entity[]
  isLoading: boolean
  count: number
  filters?: ReactElement
  autoHeight?: boolean
  checkboxSelection?: boolean
  buttons?: SensorButtons[]
  exportedFileName?: string
  processRowUpdate?: (...props: any) => any
  hideToolbar?: boolean
  refetchSensorList?: () => void
}

export const CommonSensorList: FC<CommonSensorListProps> = ({
  columns,
  data,
  isLoading,
  count,
  exportedFileName,
  filters = null,
  autoHeight = false,
  checkboxSelection = false,
  buttons = [],
  processRowUpdate = () => {},
  hideToolbar = false,
  refetchSensorList,
}) => {
  const token = useAuthorizedState()
  const [queryParams, setQueryParams] = useQueryParams(initialQueryParams)
  const apiRef = useGridApiRef()
  const [rowCountState, setRowCountState] = useState(count)
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true })
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumns>(() =>
    isPhone
      ? { left: ['uniqueId'] }
      : {
          left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "uniqueId"],
          right: ["actions"]
        }
  )
  const [selectedSensorIds, setSelectedSensorIds] = useState<GridRowId[]>([])
  const [isLoadingLabels, setIsLoadingLabels] = useState<boolean>(false)
  const [sensorBleState, setSenorBleState] = useState<BleManageState>(BleManageState.OFF)
  const addPopupMessage = useAddPopupMessage()
  const { fillMissedUplinksBulk, isLoadingBulk: isLoadingFillMissedUplinksRequest } = useFillMissedUplinks()
  const selectedSensorImeis = useMemo(() => data.filter((sensor) => selectedSensorIds.includes(sensor.id)).map((sensor) => sensor.imei), [selectedSensorIds, data])
  const selectedSensors = useMemo(
    () => data.filter((sensor) => selectedSensorIds.includes(sensor.id)).map((sensor) => ({ uniqueId: sensor.uniqueId, imei: sensor.imei })),
    [selectedSensorIds, data],
  )
  const manageSensorsBleStateRequest = useEditSensorsBleStateRequest({
    options: {
      onSuccess: (dt) => {
        const amountOfSuccess = dt.filter((item) => item.status === 'ok').length
        const failedItems = dt.filter((item) => item.status !== 'ok')
        const amountOfFailed = failedItems.length
        const messages: PopupMessage[] = []
        if (amountOfSuccess > 0) {
          messages.push({
            text: `Successfully updated - ${amountOfSuccess} sensors.`,
            type: 'success',
          })
        }
        if (amountOfFailed > 0) {
          for (const item of failedItems) {
            messages.push({
              text: `Failed to update sensor - ${item.sensorImei}. ${item.message}`,
              type: 'error',
            })
          }
        }
        addPopupMessage(messages)
        if (amountOfFailed === 0) closeSensorBleDialog()
      },
      onError: (error) => {
        addPopupMessage({ text: error.message, type: "error" })
      }
    }
  })
  const { openDialog: openSensorBleDialog, closeDialog: closeSensorBleDialog } = useDialog(
    DialogNames.ManageSensorBle
  )
  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      count !== undefined ? count : prevRowCountState
    )
  }, [count, setRowCountState])

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const newPage = pagination.page + 1
    setQueryParams({
      [SearchParamsKeys.Page]: `${newPage}`,
      [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`,
    }, [
      SearchParamsKeys.Page,
      SearchParamsKeys.RowsPerPage,
    ])
  }

  const handlePinnedColumnsChange = useCallback(
    (updatedPinnedColumns: GridPinnedColumns) => {
      setPinnedColumns(updatedPinnedColumns)
    },
    []
  )

  const handleChangeState = async () => {
    const filterModel = gridFilterModelSelector(apiRef.current.state)
    if (filterModel.items.length > 0 && filterModel.items[0].value) {
      const visibleRowCount = gridFilteredTopLevelRowCountSelector(apiRef.current.state)
      if (visibleRowCount) setRowCountState(visibleRowCount)
    }
    else setRowCountState(count)
  }

  const downloadLabels = async () => {
    if (!selectedSensorIds || selectedSensorIds.length === 0) return

    try {
      setIsLoadingLabels(true)
      const { apiPath, host } = config
      const basePath = `${host}/${apiPath}`
      const sensorUniqueIds = data.filter((sensor) => selectedSensorIds.includes(sensor.id)).map((sensor) => sensor.uniqueId)
      const response = await fetch(`${basePath}/v1/admin/sensors/generate-labels?sensorUniqueIds=${sensorUniqueIds.join(',')}&Authorization=${token}`, {
        headers: {
          'User-Agent': navigator.userAgent,
          'client-platform': 'admin'
        }
      })
      const file = await response.blob()
      const href = window.URL.createObjectURL(file)
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `labels_${new Date().toISOString()}.zip`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error(error)
      addPopupMessage({ text: 'Error during downloading labels', type: "error" })
    } finally {
      setIsLoadingLabels(false)
    }
  }

  const onFillMissedUplinks = (date: string) => {
    if (!selectedSensorIds || selectedSensorIds.length === 0) return

    const imeis = data.filter((sensor) => selectedSensorIds.includes(sensor.id)).map((sensor) => String(sensor.imei))
    fillMissedUplinksBulk(date, imeis)
  }

  const turnOnBle = () => {
    setSenorBleState(BleManageState.ON)
    openSensorBleDialog()
  }

  const turnOffBle = () => {
    setSenorBleState(BleManageState.OFF)
    openSensorBleDialog()
  }

  const manageSensorBle = () => {
    manageSensorsBleStateRequest.mutate({
      isEnabledBle: sensorBleState === BleManageState.ON,
      sensorImeis: selectedSensorImeis,
    })
  }

  const renderButtons = (): ReactElement | null => {
    if (selectedSensorIds.length === 0) return null

    const elements: ReactElement[] = []

    if (buttons.includes(SensorButtons.DownloadLabels)) {
      elements.push(<Box pt={2}>
        <Button
          size="large"
          variant="contained"
          color="primary"
          type="submit"
          disabled={isLoadingLabels}
          onClick={downloadLabels}
        >
          {
            isLoadingLabels ? <CircularProgress color="info" size={26} /> : 'Download archive with labels'
          }
        </Button>
      </Box>)
    }

    if (buttons.includes(SensorButtons.GetHistoricalData)) {
      elements.push(<Box pt={2}>
        <SensorHistoricalDataModal
          onFillMissedUplinks={onFillMissedUplinks}
          isLoading={isLoadingFillMissedUplinksRequest}
        />
      </Box>)
    }

    if (buttons.includes(SensorButtons.QualityControl)) {
      elements.push(<Box pt={2}>
        <SensorQualityControlButton selectedSensors={selectedSensors} />
      </Box>)
    }

    if (buttons.includes(SensorButtons.Assign)) {
      elements.push(<Box pt={2}>
        <BulkAssignSensorsButton selectedSensors={selectedSensors} onSuccess={refetchSensorList} />
      </Box>)
    }

    if (buttons.includes(SensorButtons.MakeReadyToUse)) {
      elements.push(<Box pt={2}>
        <MakeSensorsReadyToUseModal selectedSensors={selectedSensors} onSuccess={refetchSensorList} />
      </Box>)
    }

    if (buttons.includes(SensorButtons.ManageSensorBleState)) {
      elements.push(<Box pt={2}>
        <Button
          size="large"
          variant="contained"
          color="primary"
          type="submit"
          onClick={() => turnOnBle()}
        >
          Turn On BLE
        </Button>
      </Box>)
      elements.push(<Box pt={2}>
        <Button
          size="large"
          variant="contained"
          color="primary"
          type="submit"
          onClick={() => turnOffBle()}
        >
          Turn Off BLE
        </Button>
      </Box>)
    }

    return (
      <Grid container item xs="auto" spacing={2}>
        {elements.map((element, index) => (
          <Grid key={index} item xs="auto">{element}</Grid>
        ))}
      </Grid>
    )
  }

  const page = queryParams["page"] ? parseInt(queryParams["page"], 10) - 1 : defaultPage - 1
  const pageSize = parseInt(queryParams["rowsPerPage"], 10) || defaultPerPage

  const showFilters = !!filters || selectedSensorIds.length > 0

  return (
    <Box height='100%'>
      { showFilters &&
          <Grid container item flexDirection="column" justifyContent="space-between" mb={2}>
            {filters && <Grid item xs={selectedSensorIds.length ? "auto" : 12}>{filters}</Grid>}
            {renderButtons()}
          </Grid>
      }
      <Paper sx={{ flexGrow: 1, minHeight: '200px', height: '100%', maxHeight: '800px' }}>
        <DataGridPremium<Sensor | ThingspaceSensor>
          apiRef={apiRef}
          pagination
          autoHeight={autoHeight}
          pinnedColumns={pinnedColumns}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick
          onRowSelectionModelChange={setSelectedSensorIds}
          rows={data}
          columns={columns}
          rowCount={rowCountState}
          loading={isLoading}
          paginationModel={{ page, pageSize }}
          rowThreshold={2}
          columnThreshold={2}
          paginationMode="server"
          pageSizeOptions={defaultRowsPerPageOptions}
          onPaginationModelChange={handleChangePaginationModel}
          onStateChange={handleChangeState}
          localeText={{
            columnMenuSortAsc: "Sort A-Z",
            columnMenuSortDesc: "Sort Z-A",
          }}
          slots={{
            toolbar: hideToolbar ? null : () => <TableToolbar exportedFileName={exportedFileName} />,
          }}
          onPinnedColumnsChange={handlePinnedColumnsChange}
          sx={(theme) => ({
            border: "none",
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              paddingLeft: "5px",
              overflow: "visible",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: 1,
              fontSize: "13px",
              fontWeight: 400,
              color: "rgba(0, 0, 0, 0.6)",
              overflow: "visible",
            },
          })}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => {
            if (error) addPopupMessage({ text: error, type: "error" })
          }}
        />
      </Paper>
      <ManageSensorBleModal
        onSubmit={manageSensorBle}
        onClose={closeSensorBleDialog}
        bleState={sensorBleState}
        isLoading={manageSensorsBleStateRequest.isLoading}
        deviceId={selectedSensorImeis || ''}
      />
    </Box>
  )
}
